import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";
import { requireSupabaseAuth } from "@/integrations/supabase/auth-middleware";
import { assertBetaAccess } from "@/lib/beta-access";
import { assertCanManage, requireCompany } from "@/lib/company-access";

// CRM integrations: configure the signed lead webhook (Settings →
// Integrations). Every captured lead is POSTed to the URL with an
// HMAC-SHA256 signature over the body using the company's webhook secret.
//
// Webhook URL and secret are NOT exposed to the anon/authenticated roles
// through the Data API (SELECT on those columns is revoked). All reads and
// writes here go through supabaseAdmin AFTER we've verified via the
// authenticated context that the caller owns/manages the company.

export const getIntegrations = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    await assertBetaAccess(context.supabase, context.userId);
    const access = await requireCompany(context.supabase, context.userId);
    const canManage = access.role !== "member";
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const { data } = await supabaseAdmin
      .from("companies")
      .select("webhook_url, webhook_secret")
      .eq("id", access.id)
      .maybeSingle();
    return {
      webhookUrl: data?.webhook_url ?? "",
      // The HMAC signing secret is only ever revealed to owners/admins —
      // the same roles allowed to change or test the webhook.
      webhookSecret: canManage ? (data?.webhook_secret ?? null) : null,
      role: access.role,
    };
  });


export const updateWebhook = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d) =>
    z
      .object({
        url: z.string().trim().url().max(1000).or(z.literal("")),
        regenerateSecret: z.boolean().default(false),
      })
      .parse(d),
  )
  .handler(async ({ context, data }) => {
    await assertBetaAccess(context.supabase, context.userId);
    const access = await requireCompany(context.supabase, context.userId);
    assertCanManage(access);

    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");

    const patch: { webhook_url: string | null; webhook_secret?: string } = {
      webhook_url: data.url === "" ? null : data.url,
    };
    if (data.regenerateSecret || data.url !== "") {
      const { data: existing } = await supabaseAdmin
        .from("companies")
        .select("webhook_secret")
        .eq("id", access.id)
        .maybeSingle();
      if (data.regenerateSecret || !existing?.webhook_secret) {
        const { newWebhookSecret } = await import("@/lib/webhooks.server");
        patch.webhook_secret = newWebhookSecret();
      }
    }

    const { error } = await supabaseAdmin.from("companies").update(patch).eq("id", access.id);
    if (error) throw error;

    const { data: updated } = await supabaseAdmin
      .from("companies")
      .select("webhook_url, webhook_secret")
      .eq("id", access.id)
      .maybeSingle();
    return {
      webhookUrl: updated?.webhook_url ?? "",
      webhookSecret: updated?.webhook_secret ?? null,
    };
  });

export const sendTestWebhook = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    await assertBetaAccess(context.supabase, context.userId);
    const access = await requireCompany(context.supabase, context.userId);
    assertCanManage(access);
    const { dispatchLeadWebhook } = await import("@/lib/webhooks.server");
    const result = await dispatchLeadWebhook(access.id, {
      name: "Test Lead",
      email: "test@realitytwin.io",
      question: "This is a test event from Reality Twin — your integration works.",
      intent_score: 90,
      lead_score: 90,
      status: "hot",
      source: "test",
    });
    if (!result.delivered) {
      throw new Error(
        result.error ??
          (result.status
            ? `Endpoint responded with HTTP ${result.status}`
            : "No webhook URL configured"),
      );
    }
    return { ok: true, status: result.status };
  });
